import { useMemo } from 'react'
import { useDashboardContext } from '@/context-state/DashboardContext.jsx'
import { useCurrentSpace } from '@/hooks/useCurrentSpace.jsx'
import { IntegrationProviders } from '@/consts'

const normalizeProvider = (provider) => (
    provider
        ? String(provider)
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '_')
            .replace(/^_+|_+$/g, '')
        : null
)

export const useRelevantIntegration = (providerOverride = null, adAccountIdOverride = null) => {
    const { integrationsContext, adAccountsContext } = useDashboardContext()
    const { integrations } = integrationsContext
    const { chosenAdAccount } = adAccountsContext
    const { space } = useCurrentSpace()

    // Resolve against a specific ad account when given (e.g. the opened creative's own account),
    // otherwise fall back to the globally-chosen one. This keeps per-creative views correct in
    // multi-account reports where the global selection may belong to a different provider.
    const effectiveAdAccountId = adAccountIdOverride || chosenAdAccount

    const selectedAdAccount = useMemo(() => {
        if (!space?.adAccounts?.length || !effectiveAdAccountId) {
            return null
        }
        return space.adAccounts.find(adAccount => adAccount.id === effectiveAdAccountId) || null
    }, [space?.adAccounts, effectiveAdAccountId])

    const providerKey = useMemo(() => {
        if (providerOverride) {
            return providerOverride
        }
        if (selectedAdAccount?.provider) {
            return selectedAdAccount.provider
        }
        return null
    }, [providerOverride, selectedAdAccount?.provider])

    const integration = useMemo(() => {
        if (!integrations?.length) {
            return null
        }
        if (!providerKey) {
            // Exclude Shopify, TripleWhale and Northbeam (attribution-only, not native ad providers) from the list of integrations
            return integrations.find(item => {
                const p = normalizeProvider(item.provider)
                return p !== IntegrationProviders.SHOPIFY && p !== 'triplewhale' && p !== 'northbeam'
            }) || null
        }
        const providerNormalized = normalizeProvider(providerKey)
        return integrations.find(item => normalizeProvider(item.provider) === providerNormalized) || null
    }, [integrations, providerKey])

    const resolvedProvider = providerKey || integration?.provider || null
    const selectedAdAccountWithProvider = selectedAdAccount && !selectedAdAccount.provider && resolvedProvider
        ? { ...selectedAdAccount, provider: resolvedProvider }
        : selectedAdAccount

    const providerNormalized = normalizeProvider(resolvedProvider)
    const isMeta = providerNormalized === 'meta'
    const isTikTok = providerNormalized === 'tiktok_ads'
    const isGoogle = providerNormalized === 'google' || providerNormalized === 'google_ads'
    const isAxon = providerNormalized === 'axon'
    const isLinkedIn = providerNormalized === 'linkedin_ads'

    return {
        integration,
        provider: resolvedProvider,
        selectedAdAccount: selectedAdAccountWithProvider,
        chosenAdAccount,
        isMeta,
        isTikTok,
        isGoogle,
        isAxon,
        isLinkedIn,
        integrationProvider: providerNormalized
    }
}
